// Simple-tab presets for the Super Admin Audio Enhancement page. Maps a
// friendly 0/5/10/.../35% "intensity" stop to the real ffmpeg parameters for
// each stage — mirrors DEFAULT_ENHANCEMENT_PROFILE and buildEnhancementFilters
// in functions/src/index.ts. The percentages are a proxy, not a literal unit;
// there's no physical meaning to "20% rumble cut" beyond "stronger than 15%,
// gentler than 25%". Advanced tab bypasses this entirely and edits the raw
// technical fields directly.
//
// Attack/decay for the compressor presets are deliberately slower (0.2s/0.4s)
// than the original hardcoded default (0.1s/0.2s) — faster time constants are
// the leading cause of the mid-recording "pop" this whole feature exists to fix.

export const INTENSITY_STOPS = [5, 10, 15, 20, 25, 30, 35];

export const STAGES = {
  denoise: {
    key: 'denoise',
    label: 'Noise Reduction',
    description: 'Reduces steady background hiss and hum (fans, AC, room noise) using spectral noise reduction.',
    warning: null,
  },
  highpass: {
    key: 'highpass',
    label: 'Rumble Cut',
    description: 'Removes low-frequency rumble — desk bumps, HVAC, handling noise — below the cutoff.',
    warning: null,
  },
  loudnorm: {
    key: 'loudnorm',
    label: 'Loudness',
    description: 'Brings quiet voices up to a consistent, comfortable listening level.',
    warning: null,
  },
  compand: {
    key: 'compand',
    label: 'Compressor',
    description: 'Boosts quiet passages and gently tames loud ones so volume feels even throughout.',
    warning: 'The most likely source of pops or clicks — start low, and use the test tool below before committing to a setting.',
  },
};

const DENOISE_NR_BY_PERCENT = {5: 3, 10: 6, 15: 9, 20: 12, 25: 15, 30: 18, 35: 21};

export function denoiseParams(percent) {
  if (!percent) return {enabled: false, nr: 12};
  return {enabled: true, nr: DENOISE_NR_BY_PERCENT[percent] ?? 12};
}

const HIGHPASS_FREQ_BY_PERCENT = {5: 40, 10: 60, 15: 80, 20: 100, 25: 120, 30: 150, 35: 200};

export function highpassParams(percent) {
  if (!percent) return {enabled: false, freq: 80};
  return {enabled: true, freq: HIGHPASS_FREQ_BY_PERCENT[percent] ?? 80};
}

const LOUDNORM_LUFS_BY_PERCENT = {5: -23, 10: -21, 15: -19, 20: -17, 25: -16, 30: -14, 35: -12};

export function loudnormParams(percent) {
  if (!percent) return {enabled: false, targetLUFS: -16, truePeak: -1.5, range: 11};
  return {
    enabled: true,
    targetLUFS: LOUDNORM_LUFS_BY_PERCENT[percent] ?? -16,
    truePeak: -1.5,
    range: 11,
  };
}

// Each curve is a full ffmpeg compand "points" string. Boost strength (how
// far quiet material gets pulled up) ramps from mild at 5% to the original
// aggressive default at 35%, always with the slower, gentler attack/decay.
const COMPAND_POINTS_BY_PERCENT = {
  5: '-80/-900|-45/-33|-27/-21|0/-9|20/-9',
  10: '-80/-900|-45/-29|-27/-19|0/-9|20/-9',
  15: '-80/-900|-45/-25|-27/-17|0/-8|20/-8',
  20: '-80/-900|-45/-21|-27/-15|0/-8|20/-8',
  25: '-80/-900|-45/-19|-27/-13|0/-7|20/-7',
  30: '-80/-900|-45/-17|-27/-11|0/-7|20/-7',
  35: '-80/-900|-45/-15|-27/-9|0/-7|20/-7',
};

export function compandParams(percent) {
  if (!percent) {
    return {enabled: false, attack: 0.2, decay: 0.4, points: COMPAND_POINTS_BY_PERCENT[35]};
  }
  return {
    enabled: true,
    attack: 0.2,
    decay: 0.4,
    points: COMPAND_POINTS_BY_PERCENT[percent] ?? COMPAND_POINTS_BY_PERCENT[35],
  };
}

export function paramsForStage(stageKey, percent) {
  switch (stageKey) {
    case 'denoise': return denoiseParams(percent);
    case 'highpass': return highpassParams(percent);
    case 'loudnorm': return loudnormParams(percent);
    case 'compand': return compandParams(percent);
    default: return {enabled: false};
  }
}

// Reverse lookup — given a stage's raw saved params, find the exact matching
// preset percent stop, so the preset dropdown always tells the truth about
// what's actually saved. Returns 0 when the stage is off, a percent when the
// raw values exactly match one of the presets below, or null when they don't
// match anything (i.e. someone hand-edited the raw numbers) — callers should
// render that as "Custom" rather than guessing at a nearby percent.
export function percentForStage(stageKey, rawParams) {
  if (!rawParams?.enabled) return 0;
  if (stageKey === 'denoise') {
    return exactStop(rawParams.nr, DENOISE_NR_BY_PERCENT);
  }
  if (stageKey === 'highpass') {
    return exactStop(rawParams.freq, HIGHPASS_FREQ_BY_PERCENT);
  }
  if (stageKey === 'loudnorm') {
    return exactStop(rawParams.targetLUFS, LOUDNORM_LUFS_BY_PERCENT);
  }
  if (stageKey === 'compand') {
    if (rawParams.attack !== 0.2 || rawParams.decay !== 0.4) return null;
    const match = Object.entries(COMPAND_POINTS_BY_PERCENT).find(([, v]) => v === rawParams.points);
    return match ? Number(match[0]) : null;
  }
  return null;
}

function exactStop(value, byPercent) {
  const match = Object.entries(byPercent).find(([, stopValue]) => stopValue === value);
  return match ? Number(match[0]) : null;
}
